import React, { useState } from 'react';
import { X, Users, User, RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Candidate } from '../../lib/types/api';
import { RecruiterMultiSelector } from './RecruiterMultiSelector';

interface BulkAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (candidateIds: string[], strategy: 'single' | 'round_robin' | 'open_to_all', recruiterId?: string, recruiterList?: string[], reason?: string) => void;
  candidates: Candidate[];
  recruiters?: Array<{ id: string; name: string; email: string }>;
  isLoading?: boolean;
}

type AssignmentStrategy = 'single' | 'round_robin' | 'open_to_all';

export const BulkAssignmentModal: React.FC<BulkAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  candidates = [],
  recruiters = [],
  isLoading = false
}) => {
  const [strategy, setStrategy] = useState<AssignmentStrategy>('single');
  const [selectedRecruiterId, setSelectedRecruiterId] = useState<string>('');
  const [selectedRecruiterIds, setSelectedRecruiterIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    const candidateIds = candidates.map(c => c.candidate_id || c.id).filter(id => id != null);
    if (candidateIds.length === 0) return;
    
    const recruiterList = strategy === 'round_robin' && selectedRecruiterIds.length > 0 
      ? selectedRecruiterIds 
      : undefined;
    
    onSubmit(candidateIds, strategy, selectedRecruiterId || undefined, recruiterList, reason || undefined);
  };

  const handleClose = () => {
    setStrategy('single');
    setSelectedRecruiterId('');
    setSelectedRecruiterIds([]);
    setReason('');
    onClose();
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="glass-card rounded-2xl border border-glass-border/40 shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-glass-border/40">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-enterprise-primary">
                  Bulk Assign Candidates
                </h2>
                <p className="text-sm text-text-enterprise-tertiary">
                  {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 hover:bg-glass-surface/50 rounded-lg transition-colors group disabled:opacity-50"
              title="Close"
            >
              <X className="w-5 h-5 text-text-enterprise-tertiary group-hover:text-text-enterprise-primary" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
            {/* Candidate Selection Info */}
            <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/30">
              <p className="text-sm text-text-enterprise-primary">
                <span className="font-medium">{candidates.length}</span> candidate{candidates.length !== 1 ? 's' : ''} selected for assignment
              </p>
            </div>

            {/* Assignment Strategy */}
            <div>
              <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                Assignment Strategy
              </label>
              <div className="space-y-2">
                {/* Single Recruiter */}
                <div
                  onClick={() => setStrategy('single')}
                  className={cn(
                    "p-4 rounded-xl border cursor-pointer transition-colors",
                    strategy === 'single'
                      ? "border-primary-500/50 bg-primary-500/10"
                      : "border-glass-border/40 bg-glass-surface/30 hover:bg-glass-surface/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                      strategy === 'single'
                        ? "bg-gradient-to-r from-blue-500 to-purple-500 border-transparent"
                        : "border-glass-border/50"
                    )}>
                      {strategy === 'single' && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-primary-400" />
                        <span className="text-sm font-medium text-text-enterprise-primary">
                          Single Recruiter
                        </span>
                      </div>
                      <p className="text-xs text-text-enterprise-tertiary mt-1">
                        Assign all candidates to one recruiter
                      </p>
                    </div>
                  </div>
                </div>

                {/* Round Robin */}
                <div
                  onClick={() => setStrategy('round_robin')}
                  className={cn(
                    "p-4 rounded-xl border cursor-pointer transition-colors",
                    strategy === 'round_robin'
                      ? "border-primary-500/50 bg-primary-500/10"
                      : "border-glass-border/40 bg-glass-surface/30 hover:bg-glass-surface/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                      strategy === 'round_robin'
                        ? "bg-gradient-to-r from-blue-500 to-purple-500 border-transparent"
                        : "border-glass-border/50"
                    )}>
                      {strategy === 'round_robin' && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <RotateCcw className="w-4 h-4 text-primary-400" />
                        <span className="text-sm font-medium text-text-enterprise-primary">
                          Round Robin
                        </span>
                      </div>
                      <p className="text-xs text-text-enterprise-tertiary mt-1">
                        Distribute candidates evenly among recruiters
                      </p>
                    </div>
                  </div>
                </div>

                {/* Open to All */}
                <div
                  onClick={() => setStrategy('open_to_all')}
                  className={cn(
                    "p-4 rounded-xl border cursor-pointer transition-colors",
                    strategy === 'open_to_all'
                      ? "border-primary-500/50 bg-primary-500/10"
                      : "border-glass-border/40 bg-glass-surface/30 hover:bg-glass-surface/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                      strategy === 'open_to_all'
                        ? "bg-gradient-to-r from-blue-500 to-purple-500 border-transparent"
                        : "border-glass-border/50"
                    )}>
                      {strategy === 'open_to_all' && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary-400" />
                        <span className="text-sm font-medium text-text-enterprise-primary">
                          Open to All
                        </span>
                      </div>
                      <p className="text-xs text-text-enterprise-tertiary mt-1">
                        Any recruiter can claim these candidates
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recruiter Selection (only for single strategy) */}
            {strategy === 'single' && (
              <div>
                <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                  Select Recruiter
                </label>
                <select
                  value={selectedRecruiterId}
                  onChange={(e) => setSelectedRecruiterId(e.target.value)}
                  disabled={isLoading}
                  className={cn(
                    "w-full px-4 py-2.5 rounded-xl border border-glass-border/40",
                    "bg-glass-surface/50 backdrop-blur-sm",
                    "text-text-enterprise-primary placeholder-text-enterprise-tertiary",
                    "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                    "transition-all duration-200",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  <option value="">Select a recruiter...</option>
                  {recruiters.map((recruiter) => (
                    <option key={recruiter.id} value={recruiter.id}>
                      {recruiter.name} ({recruiter.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Multi-select recruiters for round-robin */}
            {strategy === 'round_robin' && (
              <div>
                <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                  Select Recruiters (for distribution)
                </label>
                <RecruiterMultiSelector
                  recruiters={recruiters}
                  value={selectedRecruiterIds}
                  onChange={setSelectedRecruiterIds}
                  placeholder="Search and select recruiters..."
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Reason Field */}
            <div>
              <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isLoading}
                placeholder="Add a note about this assignment..."
                rows={3}
                className={cn(
                  "w-full px-4 py-2.5 rounded-xl border border-glass-border/40",
                  "bg-glass-surface/50 backdrop-blur-sm",
                  "text-text-enterprise-primary placeholder-text-enterprise-tertiary",
                  "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                  "resize-none transition-all duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              />
            </div>
          </div>

          {/* Actions - Fixed at bottom */}
          <div className="flex-shrink-0 flex gap-3 p-6 border-t border-glass-border/40 bg-glass-surface">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-xl border border-glass-border/40",
                "bg-glass-surface/50 backdrop-blur-sm",
                "text-text-enterprise-primary font-medium",
                "hover:bg-glass-surface transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || candidates.length === 0 || (strategy === 'single' && !selectedRecruiterId) || (strategy === 'round_robin' && selectedRecruiterIds.length === 0)}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-xl font-medium",
                "bg-gradient-to-r from-blue-500 to-purple-500",
                "text-white hover:shadow-lg hover:scale-[1.02]",
                "transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                isLoading && "opacity-70"
              )}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Assigning...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Users className="w-4 h-4" />
                  Assign {candidates.length} Candidate{candidates.length !== 1 ? 's' : ''}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
